// ============================================================================
// BESPOKIAN Health - About Page
// Company story with luxury aesthetic
// ============================================================================

import { useState } from 'react';
import { Navigation } from '@/components/layout/Navigation';
import { Footer } from '@/components/layout/Footer';
import { Link } from '@tanstack/react-router';
import { Heart, Shield, Eye, Users, ArrowRight, MessageCircle, ChevronDown } from 'lucide-react';

const values = [
  { icon: Eye, title: 'Radical Transparency', description: 'See exactly what you pay for. No hidden fees, ever.' },
  { icon: Heart, title: 'Patient First', description: 'Every decision we make starts with your health and wallet.' },
  { icon: Shield, title: 'Uncompromising Quality', description: 'Every prescription reviewed by a licensed pharmacist, proper storage, careful handling.' },
  { icon: Users, title: 'Accessible Care', description: 'Fair prices so medication is affordable for everyone.' },
];

const missionStats = [
  { value: 'Up to 80%', label: 'Potential Savings', bgImage: '/img/save-30-50-bg.jpg' },
  { value: '50', label: 'States', subLabel: '(In Licensing)', bgImage: '/img/happier-employees-bg.jpg' },
  { value: 'Licensed', label: 'Pharmacist Support', bgImage: '/img/clear-reporting-bg.jpg', comingSoon: true },
  { value: 'HIPAA', label: 'Compliant Protocols', bgImage: '/img/fully-compliant-bg.jpg', comingSoon: true },
];

export function AboutPage() {
  const [missionExpanded, setMissionExpanded] = useState(false);

  const scrollToContent = () => {
    const contentSection = document.getElementById('about-content');
    if (contentSection) {
      contentSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Navigation />

      <main className="flex-1">
        {/* Hero Section with Chemistry Lab Background */}
        <section
          className="relative h-screen flex items-center justify-center overflow-hidden"
          style={{
            backgroundImage: 'linear-gradient(rgba(0, 21, 58, 0.55), rgba(0, 21, 58, 0.9)), url("/img/heroes/health-about-hero.webp")',
            backgroundSize: 'cover',
            backgroundPosition: 'center center'
          }}
        >
          <div className="relative z-10 w-full max-w-4xl mx-auto px-6 text-center">
            <div className="hidden md:block w-24 h-0.5 bg-white mx-auto mb-8" />
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-serif text-white mb-6 md:mb-8 leading-tight tracking-tight">
              About
            </h1>
            <div className="hidden md:block w-24 h-0.5 bg-white mx-auto mb-12" />

            {/* Learn More - Scroll to content section */}
            <div className="max-w-xl mx-auto">
              <div
                id="learn-more"
                role="button"
                tabIndex={0}
                onClick={scrollToContent}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') scrollToContent();
                }}
                className="bg-white/10 backdrop-blur-md rounded-lg px-6 py-4 border border-white/20 shadow-xl ring-1 ring-white/10 cursor-pointer hover:bg-white/20 transition-all duration-300 w-full"
                aria-label="Learn more about us"
              >
                <div className="flex items-center justify-between gap-6">
                  <div className="text-left">
                    <div className="text-white/90 text-xs uppercase tracking-widest mb-2">
                      LEARN MORE
                    </div>
                    <div className="text-white/70 text-lg">
                      See how it works
                    </div>
                  </div>
                  <MessageCircle className="h-6 w-6 text-white/80 flex-shrink-0" aria-hidden="true" />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* About Content Section */}
        <div id="about-content">
          {/* Hero Section */}
          <section className="bg-gradient-to-br from-navy-50 via-white to-gold-50/30 py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            {/* Gold accent line */}
            <div className="w-16 h-0.5 bg-gold-500 mx-auto mb-6" />

            <p className="text-gold-600 font-medium tracking-wide uppercase text-sm mb-4">
              Our Story
            </p>

            <h1 className="font-display text-4xl md:text-5xl text-navy-800 tracking-tight leading-tight mb-6 italic">
              Pharmacy Pricing Should Be Simple
            </h1>

            <p className="text-xl text-navy-600 max-w-2xl mx-auto">
              We started BESPOKIAN Health because people deserve to know what they're paying for. 
              Transparency isn't a feature — it's our foundation.
            </p>
          </div>
        </section>

        {/* Our Mission: Deconstructing the Black Box Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="w-12 h-0.5 bg-gold-500 mb-10 mx-auto" />

            <div className="border border-navy-100 rounded-xl overflow-hidden mb-10 bg-white">
              <button
                type="button"
                onClick={() => setMissionExpanded((e) => !e)}
                className="w-full relative flex items-center justify-center gap-4 px-6 py-4 hover:bg-navy-50/50 transition-colors"
                aria-expanded={missionExpanded}
              >
                <h2 className="font-display text-2xl md:text-3xl text-navy-800 tracking-tight italic text-center">
                  Our Mission: Deconstructing the &ldquo;Black Box&rdquo; of Pharmacy
                </h2>
                <ChevronDown
                  className={`absolute right-6 h-6 w-6 text-gold-600 transition-transform flex-shrink-0 ${missionExpanded ? 'rotate-180' : ''}`}
                  aria-hidden
                />
              </button>
              {missionExpanded && (
                <div className="w-full space-y-6 text-lg text-navy-600 px-6 pb-6 pt-0 border-t border-navy-100 text-left">
                  <p className="pt-4">
                    We started Bespokian Health because the American pharmacy system is fundamentally broken by design. It is currently dominated by middlemen known as Pharmacy Benefit Managers (PBMs), who thrive on opacity. They negotiate secret rebates and utilize &ldquo;spread pricing&rdquo;—charging your health plan one price while paying the pharmacy another—pocketing the difference at your expense.
                  </p>
                  <p>
                    This system is built to confuse. It turns your health into a commodity and hides the true cost of care behind a wall of complexity.
                  </p>
                  <p className="font-semibold text-navy-800">
                    We believe you deserve the truth.
                  </p>
                  <p>
                    We have dismantled this archaic model to build something entirely new. We refuse to play the rebate games. We do not hide margins. Instead, we offer a &ldquo;Glass Box&rdquo; model of radical transparency.
                  </p>
                  <p>
                    Our pricing is based on a simple, immutable formula:
                  </p>
                  <ul className="list-none space-y-2 pl-0">
                    <li className="flex gap-2">
                      <span className="text-gold-500 font-medium">•</span>
                      <span><strong className="text-navy-800">True Cost:</strong> You pay exactly what we pay for the medication.</span>
                    </li>
                    <li className="flex gap-2">
                      <span className="text-gold-500 font-medium">•</span>
                      <span><strong className="text-navy-800">Fair Margin:</strong> We add 15% to cover our operations (capped at $300, so you are never penalized for needing expensive care).</span>
                    </li>
                    <li className="flex gap-2">
                      <span className="text-gold-500 font-medium">•</span>
                      <span><strong className="text-navy-800">Service Fee:</strong> A flat $5 dispensing fee per prescription.</span>
                    </li>
                  </ul>
                  <p>
                    No hidden layers. No secret profits. Just the clarity and respect you deserve.
                  </p>
                </div>
              )}
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {missionStats.map((stat) => (
                <div
                  key={stat.label}
                  className="group rounded-xl p-6 pb-[50px] border border-navy-100 transition-all duration-300 ease-out relative overflow-hidden bg-navy-50 flex flex-col hover:scale-[1.02] hover:shadow-[0_20px_25px_-5px_rgba(0,0,0,0.25),0_8px_10px_-6px_rgba(0,0,0,0.2)]"
                  style={{
                    backgroundImage: `linear-gradient(rgba(0, 21, 58, 0.75), rgba(0, 21, 58, 0.85)), url("${stat.bgImage}")`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                  }}
                >
                  <div className="flex-1 min-h-0 text-center">
                    <h3 className="font-bold text-white text-4xl md:text-5xl mb-2 relative z-10">
                      {stat.value}
                    </h3>
                    <p className="text-navy-200 text-sm relative z-10">{stat.label}</p>
                    {stat.subLabel && (
                      <p className="text-navy-300/90 text-xs relative z-10 mt-0.5">{stat.subLabel}</p>
                    )}
                    {stat.comingSoon && (
                      <p className="text-navy-300/90 text-xs relative z-10 mt-0.5">(Coming Soon)</p>
                    )}
                  </div>
                  <div
                    className="mission-card-ribbon absolute bottom-0 left-0 right-0 rounded-b-xl"
                    aria-hidden
                  />
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Values Section */}
        <section className="py-20 bg-navy-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <div className="w-16 h-0.5 bg-gold-500 mx-auto mb-6" />
              <h2 className="font-display text-3xl text-navy-800 tracking-tight italic mb-4">Our Values</h2>
              <p className="text-navy-600 max-w-2xl mx-auto">
                These principles guide every decision we make.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {values.map((value) => (
                <div key={value.title} className="bg-white rounded-xl p-6 shadow-card hover:shadow-card-hover transition-shadow">
                  <div className="w-12 h-12 bg-gold-100 rounded-xl flex items-center justify-center mb-4">
                    <value.icon className="h-6 w-6 text-gold-600" />
                  </div>
                  <h3 className="font-semibold text-navy-800 mb-2">{value.title}</h3>
                  <p className="text-navy-600 text-sm">{value.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Why Different Section */}
        <section className="py-20 bg-white">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <div className="w-16 h-0.5 bg-gold-500 mx-auto mb-6" />
              <h2 className="font-display text-3xl text-navy-800 tracking-tight italic mb-4">Why We're Different</h2>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-coral-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-coral-600">✗</span>
                </div>
                <h3 className="font-semibold text-navy-800 mb-2">No PBM Games</h3>
                <p className="text-navy-600 text-sm">
                  We don't participate in rebate schemes or spread pricing. What you see is what you pay.
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-sage-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-sage-600">✓</span>
                </div>
                <h3 className="font-semibold text-navy-800 mb-2">Real Pharmacists</h3>
                <p className="text-navy-600 text-sm">
                  Licensed pharmacists review every prescription and are available to answer questions about your medications.
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-gold-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-gold-600">$</span>
                </div>
                <h3 className="font-semibold text-navy-800 mb-2">Honest Pricing</h3>
                <p className="text-navy-600 text-sm">
                  Cost + 15% + $5. We show you the math. No surprises, no hidden fees, ever.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-navy-800">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="w-16 h-0.5 bg-gold-500 mx-auto mb-6" />
            <h2 className="font-display text-3xl text-white tracking-tight italic mb-6">
              Ready to Experience the Difference?
            </h2>
            <p className="text-xl text-navy-200 mb-8">
              Experience fair pricing for your medications.
            </p>
            <Link to="/login">
              <button className="px-8 py-4 text-lg font-semibold text-navy-800 bg-gold-500 hover:bg-gold-400 rounded-lg shadow-lg transition-all hover:shadow-glow-gold inline-flex items-center justify-center gap-2">
                Get Started <ArrowRight className="h-5 w-5" />
              </button>
            </Link>
          </div>
        </section>
        </div>
      </main>

      <Footer />
    </div>
  );
}

